/**
 * Passkeys STOCKEES dans une entree du coffre (item R13, partie modelisable).
 *
 * A ne pas confondre avec `passkey-unlock.ts` : la, une passkey ouvre le
 * coffre sur CET appareil ; ici, le coffre GARDE les passkeys des comptes de
 * l'utilisateur (celles que Apple Passwords, Google Password Manager,
 * 1Password ou Bitwarden exportent en CXF) pour qu'une migration n'en perde
 * aucune. La cle privee est un secret au meme titre qu'un mot de passe : elle
 * vit UNIQUEMENT dans les secrets chiffres de l'entree (`secrets.passkeys`),
 * jamais en metadonnee, jamais affichee, jamais copiable.
 *
 * L'usage (creer/presenter une passkey a un site) demande une extension
 * navigateur ou un Credential Provider mobile — Phase 3/4. Tant qu'il n'existe
 * pas, Klavox CONSERVE et RESTITUE (export CXF) ce qu'on lui confie, et le dit
 * sans faire croire qu'il sait deja s'en servir.
 *
 * Module pur : zero E/S, zero reseau.
 */
import type { EntrySecrets } from '@klavox/crypto-core';

/**
 * Passkey telle que la definit le CXF v1.0 (§Passkey). Les champs publics
 * (`rpId`, `userName`…) identifient le compte ; `privateKey` est le secret.
 */
export interface PasskeyCredential {
  /** Identifiant WebAuthn du credential, base64url (donnee publique). */
  readonly credentialId: string;
  /** Domaine du service (`rpId` WebAuthn), ex. `github.com`. */
  readonly rpId: string;
  /** Identifiant de l'utilisateur chez ce service. */
  readonly userName: string;
  /** Nom affiche par le service ; souvent vide. */
  readonly userDisplayName: string;
  /** `user.id` WebAuthn, base64url (donnee publique, opaque). */
  readonly userHandle: string;
  /** Cle privee PKCS#8 (DER) en base64url. SECRET — jamais affichee ni copiee. */
  readonly privateKey: string;
  /** Creation, en millisecondes (le CXF l'exprime en secondes). */
  readonly createdAt: number;
  /**
   * Extensions FIDO2 (`hmac-secret`, `credBlob`…) telles que recues. Conservees
   * verbatim : les jeter rendrait la passkey inutilisable chez le prochain
   * gestionnaire, silencieusement.
   */
  readonly fido2Extensions?: Readonly<Record<string, unknown>>;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function extensions(value: unknown): Readonly<Record<string, unknown>> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * Passkeys d'une entree, plus recente d'abord. Une passkey sans identifiant ou
 * sans cle privee n'est pas une passkey : elle est ignoree plutot que d'etre
 * affichee comme utilisable (les elements mal formes viennent d'un import ou
 * d'un serveur, jamais de l'UI).
 */
export function readPasskeys(secrets: EntrySecrets): PasskeyCredential[] {
  const raw = secrets['passkeys'];
  if (!Array.isArray(raw)) return [];
  const list: PasskeyCredential[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== 'object') continue;
    const source = item as Record<string, unknown>;
    const credentialId = text(source['credentialId']);
    const privateKey = text(source['privateKey']);
    if (credentialId.length === 0 || privateKey.length === 0) continue;
    const createdAt = Number(source['createdAt']);
    const fido2Extensions = extensions(source['fido2Extensions']);
    list.push({
      credentialId,
      rpId: text(source['rpId']),
      userName: text(source['userName']),
      userDisplayName: text(source['userDisplayName']),
      userHandle: text(source['userHandle']),
      privateKey,
      createdAt: Number.isSafeInteger(createdAt) && createdAt >= 0 ? createdAt : 0,
      ...(fido2Extensions !== undefined ? { fido2Extensions } : {}),
    });
  }
  return list.sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Liste normalisee : validee, plus recente d'abord, sans doublon. Deux
 * gestionnaires peuvent exporter la meme passkey (meme `rpId` + meme
 * `credentialId`) — la garder deux fois ferait croire a deux comptes.
 */
export function normalizePasskeys(passkeys: readonly PasskeyCredential[]): PasskeyCredential[] {
  const seen = new Set<string>();
  return readPasskeys({ passkeys }).filter((passkey) => {
    const key = `${passkey.rpId}\0${passkey.credentialId}`;
    return !seen.has(key) && seen.add(key) !== undefined;
  });
}

/** Secrets avec cette liste de passkeys ; une liste vide retire la cle plutot que d'ecrire `[]`. */
export function withPasskeys(secrets: EntrySecrets, passkeys: readonly PasskeyCredential[]): EntrySecrets {
  const normalized = normalizePasskeys(passkeys);
  const { passkeys: _dropped, ...rest } = secrets;
  return normalized.length > 0 ? { ...rest, passkeys: normalized } : rest;
}

/** Libelle affichable d'une passkey — jamais la cle privee. */
export function passkeyLabel(passkey: PasskeyCredential): string {
  return passkey.userName || passkey.userDisplayName || passkey.rpId || 'Passkey';
}
